'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import LandingReveal from './LandingReveal';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import {
  PAPER_BAND,
  PAPER_INK,
  PAPER_RULE,
  PAPER_FOCUS,
  PAPER_VARIANT,
  PAPER_ACCENT_INK,
} from '../ui/paper-tokens';

// R8 review FIX 5 (2026-08-12): the mono kicker this section grew in the R8
// polish pass is REMOVED. It read the nav's `#faq` anchor label ("Questions" /
// "Întrebări") directly above an H2 that already says "Frequently asked
// questions" — an eyebrow that restates its own heading's subject adds a
// second type node and zero information. Nothing to delete in content.ts: the
// kicker never had its own leaf, it borrowed `nav.links`, which the navbar
// still owns and renders. Everything else from that pass stays (serif question
// type, larger hit areas, hover/active wash, the transform-based reveal).

function FAQSectionInner() {
  const { faq } = LANDING_CONTENT;
  const { t } = useLocale();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      id={faq.id}
      // Assurance chapter band, shared with Trust — no hairline between the
      // two, they read as one chapter (R6: DARK_BAND.section warm retint;
      // R7 hallmark paper flip: PAPER_BAND.alt — the shared chapter step,
      // one rung below the page's own base band; flagged for integration's
      // full-page band-ladder pass to confirm/reassign). R5-P3 Lane L6
      // (design amendment §7 one-voice conformance matrix): pad-y normalized
      // to 80px mobile -> 96px lg (was pt-16/md:pt-20 + pb-12/md:pb-16, an
      // asymmetric top-heavy ramp on a `md:` breakpoint) — still tighter than
      // Pricing/Final CTA's 96->128px "breathe" rhythm either side of this
      // chapter, just on the matrix's own two-point mobile->lg scale instead
      // of the old Judge-3 table. No incoming border-t: the preceding
      // NarrativeHandoff bridge (#5) already owns the single seam above — a
      // second line here would double it (its exact on-paper hairline color
      // is integration's call, not re-pinned here).
      className="relative pt-20 pb-10 lg:pt-24 lg:pb-12 overflow-hidden [scroll-margin-top:80px] md:[scroll-margin-top:88px]"
    >
      <div className={`absolute inset-0 ${PAPER_BAND.alt.bg}`} aria-hidden="true" />

      {/* R2-6 (2026-07-10 review round): outer 1440 container aligns this
          section's chapter gutters with the rest of the page; max-w-6xl moves
          to an inner content wrapper so the readability measure is unchanged. */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Decision 12 "reduced motion composition": ONE group reveal around
              heading + list, delay 0 — no separately-delayed second reveal. */}
          <LandingReveal>
            {/* Section header — Assurance chapter headings are LEFT-aligned.
                R5-P3 Lane L6 (design amendment §1 "the serif display law" /
                §7 conformance matrix): H2 swaps onto the shared
                `landing-serif-display` utility (globals.css) + the global
                section-H2 ramp (34px/1.15 mobile -> 40px/1.1 lg, UP from
                this node's old 32/36/52px three-step Inter ramp), dropping
                the old `font-semibold`/`tracking-[-0.035em]` sans treatment
                in the same edit ([ADV-13]: serif ships weight 400, tracking
                normal, never the sans tight-tracking treatment). Head->body
                gap (this wrapper's bottom margin) normalizes 32/40px -> a
                flat 16px per the matrix. Color/copy/layout untouched.
                The H2 stands alone — see the FIX 5 note at the top of this
                file for why the R8 mono kicker above it was removed. */}
            <div className="max-w-[26ch] mb-4">
              <h2
                className={`landing-serif-display text-[34px] leading-[1.15] lg:text-[40px] lg:leading-[1.1] ${PAPER_INK.primary.text}`}
              >
                {t(faq.headline)}
              </h2>
            </div>

            {/* Editorial objection-handling list — border-y + divide-y rows,
                no per-row card/background/radius. Widened max-w-3xl -> max-w-4xl
                (Decision 12); content/order/schemaEligible untouched. R7 paper
                flip: outer border keeps the stronger of the two rule steps
                (PAPER_RULE.hairline, was the more-opaque #FFFFFF1A), inner
                row dividers keep the fainter step (PAPER_RULE.soft, was the
                fainter #FFFFFF12) — same two-tier hierarchy, re-inked. */}
            <div
              className={`max-w-4xl border-y ${PAPER_RULE.hairline.border} divide-y ${PAPER_RULE.soft.divide}`}
            >
              {faq.items.map((item, index) => {
                const isOpen = openIndex === index;
                const panelId = `faq-panel-${index}`;
                const triggerId = `faq-trigger-${index}`;
                return (
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- static content array from LANDING_CONTENT constant, never reordered or filtered at runtime
                  <div key={index} className="group relative">
                    {/* R4-P3 masterplan §2.7 "FAQ accordion rows consistent
                        with the StepperRail accordion grammar": the open row
                        gets the same 3px gradient left-rail the product-OS
                        rail uses for its active row (FeatureGridSection.tsx
                        [AMENDED per A2], `inset-y-[16%] w-[3px]`) — a
                        neutral (never amber) marker, additive to the
                        existing amber question/chevron affordance already
                        on disk. R7 paper flip: was a near-white fade-in
                        (`rgba(245,246,247,.15)->rgba(245,246,247,1)`, tuned
                        to read against the dark band); re-inked to
                        PAPER_INK.primary's own hex (#1C1712 -> rgb(28,23,18))
                        so the same "faint top, solid bottom" fade reads on
                        paper instead. `paper-tokens.ts` exports hex/text
                        forms only, not a decimal-rgb triplet for a gradient
                        stop — flagged for integration in case a future round
                        wants a `PAPER_COMPOUND` recipe (mirroring
                        `DARK_COMPOUND`) for this exact shape; this value is
                        computed directly from the registry's own reviewed
                        hex, not an invented tone. */}
                    {isOpen && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-y-[16%] left-0 w-[3px] rounded-full bg-[linear-gradient(to_bottom,rgba(28,23,18,.15),rgba(28,23,18,1))]"
                      />
                    )}
                    <button
                      id={triggerId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className={cn(
                        // R8 §F "generous hit areas": py bumped 22px/24px ->
                        // 24px/28px (mobile/lg), left inset 16px/20px ->
                        // 20px/24px — a larger, more deliberate tap/click
                        // target on every row, not just the open one. Right
                        // edge stays flush (unchanged) — the chevron already
                        // sits at the row's own right boundary via
                        // justify-between, matching the pre-existing layout.
                        'w-full flex items-center justify-between gap-4 py-6 pl-5 lg:py-7 lg:pl-6 text-left transition-colors duration-200',
                        // R8 §F "hover state": a hue-neutral wash (same
                        // bg-black/[0.0x] alpha-overlay convention LightEyebrow
                        // + SolutionSection's chip already use — Law #4, no
                        // chapter hue on a light band) plus an active-press
                        // step one notch darker; both no-ops while open (the
                        // row already reads as engaged via the left-rail +
                        // amber question/chevron).
                        !isOpen && 'hover:bg-black/[0.025] active:bg-black/[0.04]',
                        'focus-visible:outline-none focus-visible:ring-2',
                        PAPER_FOCUS.ring,
                        'focus-visible:ring-offset-2',
                        PAPER_VARIANT.altRingOffset
                      )}
                    >
                      {/* 2026-07-18 slop-audit revision pass FIX 1 (CRITICAL,
                          amber-on-paper contrast): the open state used to read
                          `text-[#FBBF24]` (Tailwind amber-400) directly against
                          this section's `PAPER_BAND.alt` — 1.77:1, well under
                          the WCAG AA 4.5:1 text floor. `PAPER_ACCENT_INK` is
                          the darkened, contrast-proven amber-as-text token
                          (`ui/paper-tokens.ts`, computed + tested); amber-as-
                          FILL sites (CTA buttons, focus rings — the ring two
                          lines above this span) are unaffected.
                          R8 §F "serif question type": swaps the question onto
                          the shared `landing-serif-display` utility (family +
                          weight 400 + font-synthesis:none, the same rule every
                          H2 on the page shares) in place of the old Inter
                          `font-medium` — the question now reads as the page's
                          editorial display voice, the answer stays Inter body
                          (unchanged below), matching the serif-heading/
                          Inter-body split every other chapter on the page
                          uses. */}
                      <span
                        className={cn(
                          'landing-serif-display text-[17px] leading-[26px] lg:text-[19px] lg:leading-[28px] pr-4 transition-colors duration-200',
                          isOpen ? PAPER_ACCENT_INK.text : PAPER_INK.primary.text
                        )}
                      >
                        {t(item.question)}
                      </span>
                      {/* ChevronDown rotation is the visual open/close cue; aria-expanded on the button is the a11y signal.
                        Rotation duration/ease synced to the panel morph (250ms --ease-reveal);
                        reduced-motion = chevron snaps. No per-instance hover
                        recolor here (paper-tokens.ts owns the fixed ink
                        registry and is out of this lane's ownership; the
                        button's own background-wash hover above is the row's
                        hover affordance). */}
                      <ChevronDown
                        aria-hidden="true"
                        className={cn(
                          'w-5 h-5 flex-shrink-0 transition-transform duration-[250ms] ease-[var(--ease-reveal)] motion-reduce:transition-none',
                          isOpen ? cn('rotate-180', PAPER_ACCENT_INK.text) : PAPER_INK.muted.text
                        )}
                      />
                    </button>

                    {/*
                    Grid-rows technique — animates to true content height with no cap.
                    grid-rows-[0fr] collapses the inner div to 0 height (min-h-0 + overflow-hidden
                    on the inner div is what makes the row actually collapse to 0px).
                    grid-rows-[1fr] expands to fit content naturally.
                    No transition-all; only transition-[grid-template-rows] is animated.
                  */}
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={triggerId}
                      className={cn(
                        // grid-rows 0fr→1fr is the accessible accordion height-morph
                        // (animates to true content height; transform/scaleY would
                        // squish text). It is layout-bound, so it is scoped to this
                        // below-fold, click-triggered (NOT scroll-linked) micro-anim
                        // where the per-frame layout cost is acceptable — a deliberate,
                        // bounded exception to the transform/opacity-only constraint.
                        // Opacity fades in sync with the row morph; 250ms with the
                        // house --ease-reveal token.
                        // R3-11 (2026-07-10 review round): reduced motion narrows
                        // `transition-property` to `opacity` only — grid-template-rows
                        // (not listed) snaps instantly, but the answer's opacity still
                        // fades over a shorter 180ms. Never a fully dead/instant reveal.
                        'grid transition-[grid-template-rows,opacity] duration-[250ms] ease-[var(--ease-reveal)]',
                        'motion-reduce:transition-[opacity] motion-reduce:duration-[180ms]',
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      )}
                    >
                      <div className="min-h-0 overflow-hidden">
                        {/* R8 §F "refined expand motion": the answer now also
                            rises 4px on open (transform, compositor-safe,
                            same 250ms --ease-reveal token as the row morph
                            above it — MOTION LAW-compliant: CSS transition,
                            click-triggered not scroll-linked, transform/
                            opacity only). Left inset matches the button's
                            new pl-5/lg:pl-6 hit-area padding above (was
                            pl-4/lg:pl-5) so the question and its answer stay
                            vertically aligned under the wider button inset. */}
                        <p
                          className={cn(
                            'pb-6 pl-5 lg:pl-6 text-[16px] leading-[27px] max-w-[70ch] transition-transform duration-[250ms] ease-[var(--ease-reveal)] motion-reduce:transition-none',
                            isOpen ? 'translate-y-0' : '-translate-y-1',
                            PAPER_INK.muted.text
                          )}
                        >
                          {t(item.answer)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </LandingReveal>
        </div>
      </div>
    </section>
  );
}

export default function FAQSection() {
  return <FAQSectionInner />;
}
